import React, {useEffect, useState} from 'react';
import { useHistory } from 'react-router-dom';
import { Tooltip, ClickAwayListener } from '@mui/material';
import { formatFullLocation } from "utils";
import { Location } from "components/BillingLocationSelector";
import { Subtitle, SubtitleSecondary, Title, TitleContainer, JobTitleButton, MenuOutlinedIconStyled, PostedOnText } from "./JobTitle.styles";
import { BackendJob, HygienistBackendJob } from "components/EditJob";
import Api from "api";

interface JobTitleProps {
    title: string;
    practiceName?: string;
    location?: Location;
    job?: BackendJob | HygienistBackendJob;
    showJobDetailsButton?: boolean;
    disableCardStyle?: boolean;
}

const JobTitle: React.FC<JobTitleProps> = ({title, practiceName, location, job, showJobDetailsButton = true, disableCardStyle}) => {
    const history = useHistory();
    const [tooltipOpen, setTooltipOpen] = useState(false);

    const handleJobDetailsClick = async () => {
        if (job && job.id) {
            try {
                const hireJob = await Api.getHireJob(job.id);
                if (hireJob) {
                    history.push('jobInfo', { job: hireJob });
                } else {
                    console.error("Job not found in the list of hire jobs");
                }
            } catch (error) {
                console.error("Error fetching job details:", error);
            }
        }
    };

    const handleTooltipClick = () => {
        setTooltipOpen(!tooltipOpen);
    };

    const handleTooltipClose = () => {
        setTooltipOpen(false);
    };

    useEffect(() => {
        if (!tooltipOpen) return undefined;

        const closeTooltip = () => setTooltipOpen(false);
        window.addEventListener('scroll', closeTooltip, true);
        document.addEventListener('scroll', closeTooltip, true);

        return () => {
            window.removeEventListener('scroll', closeTooltip, true);
            document.removeEventListener('scroll', closeTooltip, true);
        };
    }, [tooltipOpen]);

    // Check if practice name contains asterisks (is obfuscated)
    const isObfuscated = practiceName && practiceName.includes('*');

    // Format the posted date
    const formatPostedDate = (dateString: string) => {
        if (!dateString) {
            console.error("createdOn date string is empty or undefined");
            return ""; // Or some placeholder if you prefer
        }
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            console.error("Invalid date string for createdOn:", dateString);
            return "Invalid Date"; // Or return "" to hide it
        }
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const createdDateFormatted = job && job.createdOn ? formatPostedDate(job.createdOn) : null;

    return (
        <TitleContainer disableCardStyle={disableCardStyle}>
            <Title>{title}</Title>
            {practiceName && (
                <Subtitle>
                    {practiceName}
                    {isObfuscated && (
                        <ClickAwayListener onClickAway={handleTooltipClose}>
                            <Tooltip
                                title="Dental offices prefer to keep their practice name private in the postings, but we show you enough of the name to help prevent you from applying to an unwanted office."
                                open={tooltipOpen}
                                disableHoverListener
                                disableFocusListener
                                disableTouchListener
                                placement="bottom"
                            >
                                <span
                                    onClick={handleTooltipClick}
                                    style={{
                                        color: 'var(--primary-blue, #0B4598)',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        marginLeft: '8px',
                                        fontSize: '0.9rem'
                                    }}
                                >
                                    What's this?
                                </span>
                            </Tooltip>
                        </ClickAwayListener>
                    )}
                </Subtitle>
            )}
            {location && <SubtitleSecondary>{formatFullLocation(location)}</SubtitleSecondary>}
            {createdDateFormatted && createdDateFormatted !== "Invalid Date" && (
                <PostedOnText>Posted On: {createdDateFormatted}</PostedOnText>
            )}
            {showJobDetailsButton && job && <JobTitleButton 
                variant="outlined"
                color="primary"
                size="large"
                id="signup-button"
                onClick={handleJobDetailsClick}
                disabled={!job}
            >
                <MenuOutlinedIconStyled />
                Job Details
            </JobTitleButton>}
        </TitleContainer>
    );
}

export default JobTitle;